import {
  Box,
  Button,
  Center,
  Flex,
  ListItem,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  UnorderedList,
  Input,
  InputGroup,
  InputLeftAddon,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import UserAvatar from "components/elements/UserAvatar";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useEffect, useState } from "react";
import { apiRequest } from "utils/api";
import { borrowerOrUser, getUserTagline } from "utils/helpers";

type Contact = {
  id: string;
  first_name: string;
  last_name: string;
  user: { id: string };
  borrower?: { id: string };
  type?: string;
};

const NewConversation = ({
  exclude = [],
  loadConversations,
}: {
  exclude: string[];
  loadConversations: () => Promise<void>;
}) => {
  const { user } = useTenant();
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterBorrowerContacts, setBContacts] = useState<Contact[]>([]);

  async function loadContacts() {
    try {
      const { borrower_contacts = [] } = await apiRequest("/supabase/borrower_contacts/select");

      const isBorrower = borrowerOrUser(user.type) === "borrower";
      const userKey = borrowerOrUser(user.type); //user or borrower

      const filteredContacts = borrower_contacts.filter((contact: any) => {
        if (isBorrower) {
          const contactId = contact.id || contact;
          return !exclude.includes(contactId);
        } else {
          const contactId = contact[userKey]?.id || contact[userKey];
          return !exclude.includes(contactId);
        }
      });

      const sortedContacts = filteredContacts.sort((a: any, b: any) => {
        if (isBorrower) {
          return a.first_name.localeCompare(b.first_name);
        } else {
          return a.user.first_name.localeCompare(b.user.first_name);
        }
      });

      setContacts(sortedContacts);
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (user) {
      loadContacts();
    }
  }, [user]);

  async function createConversation(contact: any) {
    try {
      setLoading(true);
      // TODO: refactor to toggleAccountType
      const params: {
        borrower?: string;
        lender?: string;
      } = {};

      if (user.type === "borrower") {
        params.borrower = user.id;
        params.lender = contact.user.id;
      } else {
        params.borrower = contact?.borrower?.id ?? contact.id;
        // params.borrower = contact.borrower.id;
        params.lender = user.id;
      }

      await apiRequest("/supabase/conversations/insert", params);

      await loadConversations();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const filteredContact = contacts.filter((bContact: any) => {
      return (
        bContact &&
        bContact.first_name &&
        bContact.first_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
    setBContacts(filteredContact || []);
  }, [contacts, searchQuery]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Popover>
      <PopoverTrigger>
        <Button
          borderColor="gray.50"
          variant="ghost"
          borderWidth="1px"
          color="primary"
          borderRadius="80px"
          disabled={contacts.length === 0}
          rightIcon={<FontAwesomeIcon icon={["fas", "plus"]} />}
        >
          New
        </Button>
      </PopoverTrigger>
      <PopoverContent
        borderWidth="0px"
        boxShadow="0px 4px 4px rgba(204, 204, 204, 0.25), 0px 0px 15px rgba(198, 198, 198, 0.25)"
      >
        <PopoverArrow />
        <PopoverBody p="0px" maxH="280px" overflowY="scroll">
          {loading || contacts.length === 0 ? (
            <Center my="10px">{loading ? <Spinner /> : <Text>No contacts found.</Text>}</Center>
          ) : (
            <>
              <Box display="flex" justifyContent={"space-between"} mt={"10px"} ml={"-13px"}>
                <Box w={"88%"}>
                  <InputGroup
                    py="0px"
                    height="35px"
                    ml="25px"
                    mr="12px"
                    color="gray.300"
                    fontSize="14px"
                    fontWeight="500"
                    borderWidth="1.5px"
                    borderColor="#E0E0E0"
                    borderRadius="7px"
                  >
                    <InputLeftAddon height="32px" pr="0px">
                      <FontAwesomeIcon icon={["far", "magnifying-glass"]} fontSize="14px" />
                    </InputLeftAddon>
                    <Input
                      fontSize="14px"
                      fontWeight="500"
                      pl="8px"
                      py="0px"
                      height="31px"
                      lineHeight="22px"
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      placeholder="Search"
                    />
                  </InputGroup>
                </Box>
              </Box>
              <UnorderedList listStyleType="none" p="0" my="10px">
                {filterBorrowerContacts && filterBorrowerContacts.length > 0
                  ? filterBorrowerContacts.map((contact: any) => (
                      <ListItem
                        py="12px"
                        px="16px"
                        fontSize="14px"
                        color="black"
                        cursor="pointer"
                        key={contact.id}
                        _hover={{ background: "gray.50" }}
                        onClick={() => createConversation(contact)}
                      >
                        <Flex alignItems="center">
                          <UserAvatar
                            user={
                              user.type === "borrower"
                                ? contact[borrowerOrUser(user.type)]
                                : contact
                            }
                            size="37px"
                            mr="12px"
                          />
                          <Box>
                            <Text fontSize="14px">
                              {contact.first_name ?? contact[borrowerOrUser(user.type)].first_name}{" "}
                              {contact.last_name ?? contact[borrowerOrUser(user.type)].last_name}
                            </Text>
                            <Text fontSize="12px" color="#B3B7BB" mt="0px">
                              {contact?.type ?? getUserTagline(contact[borrowerOrUser(user?.type)])}
                            </Text>
                          </Box>
                        </Flex>
                      </ListItem>
                    ))
                  : contacts.map((contact: any) => (
                      <ListItem
                        py="12px"
                        px="16px"
                        fontSize="14px"
                        color="black"
                        cursor="pointer"
                        key={contact.id}
                        _hover={{ background: "gray.50" }}
                        onClick={() => createConversation(contact)}
                      >
                        <Flex alignItems="center">
                          <UserAvatar
                            user={
                              user.type === "borrower"
                                ? contact[borrowerOrUser(user.type)]
                                : contact
                            }
                            size="37px"
                            mr="12px"
                          />
                          <Box>
                            <Text fontSize="14px">
                              {contact.first_name ?? contact[borrowerOrUser(user.type)].first_name}{" "}
                              {contact.last_name ?? contact[borrowerOrUser(user.type)].last_name}
                            </Text>
                            <Text fontSize="12px" color="#B3B7BB" mt="0px">
                              {contact?.type ?? getUserTagline(contact[borrowerOrUser(user?.type)])}
                            </Text>
                          </Box>
                        </Flex>
                      </ListItem>
                    ))}
              </UnorderedList>
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default NewConversation;
